import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useAgentStream } from '../hooks/useAgentStream'
import type { AgentId } from '../config/env'
import type {
  ChatEvent,
  ClarifyingAnswerPayload,
  MessageIntent,
} from '../types'
import type { PendingAttachment } from '../lib/attachments'

interface ConversationContextType {
  agentId: AgentId
  events: ChatEvent[]
  eventsThreadId: string | undefined
  /** The UI snapshot is the hook's own concern; callers pass only these. */
  sendMessage: (
    messageText: string,
    threadId?: string,
    options?: { attachments?: PendingAttachment[]; intent?: MessageIntent }
  ) => Promise<'aborted' | 'failed' | undefined>
  /** David only: inject into the live run. Anything else queues the text. */
  steer?: (
    messageText: string,
    threadId: string,
    queueAt?: number
  ) => Promise<'enqueued' | 'queued' | 'failed' | undefined>
  submitClarifyingAnswer: (
    toolCallId: string,
    payload: ClarifyingAnswerPayload,
    threadId: string
  ) => Promise<void>
  clarifyingAnswers: Record<string, ClarifyingAnswerPayload>
  streamingState: { isStreaming: boolean }
  runFailed: boolean
  stop: (threadId?: string) => void
  detach: () => void
  clearEvents: () => void
  discardThread: (threadId: string) => void
}

const ConversationContext = createContext<ConversationContextType | undefined>(
  undefined
)

interface ConversationProviderProps {
  children: ReactNode
  agentId: AgentId
}

export function ConversationProvider({
  children,
  agentId,
}: ConversationProviderProps) {
  const {
    events,
    eventsThreadId,
    sendMessage,
    steer,
    submitClarifyingAnswer,
    clarifyingAnswers,
    streamingState,
    runFailed,
    stop,
    detach,
    clearEvents,
    discardThread,
  } = useAgentStream({
    agentId,
  })

  const contextValue = useMemo(
    () => ({
      agentId,
      events,
      eventsThreadId,
      sendMessage,
      steer: agentId === 'david' ? steer : undefined,
      submitClarifyingAnswer,
      clarifyingAnswers,
      streamingState,
      runFailed,
      stop,
      detach,
      clearEvents,
      discardThread,
    }),
    [
      agentId,
      events,
      eventsThreadId,
      sendMessage,
      steer,
      submitClarifyingAnswer,
      clarifyingAnswers,
      streamingState,
      runFailed,
      stop,
      detach,
      clearEvents,
      discardThread,
    ]
  )

  return (
    <ConversationContext.Provider value={contextValue}>
      {children}
    </ConversationContext.Provider>
  )
}

export function useConversationContext(): ConversationContextType {
  const context = useContext(ConversationContext)
  if (context === undefined) {
    throw new Error(
      'useConversationContext must be used within a ConversationProvider'
    )
  }
  return context
}
